import React, { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth, getToken } from "@/lib/authContext";
import { Footer } from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PlusCircle, Loader2, LogOut, MapPin, Heart, Bookmark, Route as RouteIcon, Star, Settings, Award, Store, Upload, Edit2, Check, X, ChevronDown, ChevronUp, Info, ArrowRight, Scan, Sparkles, CheckCircle2, Camera, Trash2, Crown, UserX, Coins } from "lucide-react";
import { ImageCaptureInput } from "@/components/ImageCaptureInput";
import { PriceLevel } from "@/components/PriceLevel";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useShopInteractions } from "@/contexts/ShopInteractionsContext";
import { useRouteInteractions } from "@/contexts/RouteInteractionsContext";
import { ShopCard } from "@/components/ShopCard";
import { RouteCard } from "@/components/RouteCard";
import { PostCard } from "@/components/PostCard";
import { UpgradeModal } from "@/components/UpgradeModal";
import { ShopDashboard } from "@/components/ShopDashboard";
import { UserBadges } from "@/components/BadgeIcon";
// ItemWishlist import removed
import { SocialSettings } from "@/components/SocialSettings";
import { PrivacyDashboard } from "@/components/PrivacyDashboard";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { Autocomplete } from "@/lib/googleMapsApiShim";
import { getGoogleMapsApiKey } from "@/lib/googleMaps";
import { useRef } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useRestyleVisibility } from "@/lib/featureFlags";
import { getLocalizedPath, LOCALE_LABELS, persistPreferredLocale, useI18n, withLocalePrefix } from "@/lib/i18n";
import type { AppLocale } from "@shared/i18n-locales";

import { useGoogleMaps } from "@/contexts/GoogleMapsContext";

export default function AccountPage() {
  const [location, setLocation] = useLocation();
  const {
    user,
    isLoggedIn,
    signOut,
    refreshAuth,
    setHomeAddress,
    tier,
    setTier
  } = useAuth();
  const { setBulkInteractions: setBulkShopInteractions } = useShopInteractions();
  const { setBulkInteractions: setBulkRouteInteractions } = useRouteInteractions();
  const { canSeeRestyle } = useRestyleVisibility();
  const { locale, t } = useI18n();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Use global Maps loader
  const { isLoaded, loadError } = useGoogleMaps();

  const [homeInput, setHomeInput] = useState("");
  const [addressDetails, setAddressDetails] = useState<any>({});
  const [newEmail, setNewEmail] = useState("");
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [isSavingUsername, setIsSavingUsername] = useState(false);
  const [isRedirectingPortal, setIsRedirectingPortal] = useState(false);
  const formatLocalizedDate = (value: string | number | Date) =>
    new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric" }).format(new Date(value));
  const canUsePlacesAutocomplete =
    isLoaded &&
    typeof window !== "undefined" &&
    typeof window.google?.maps?.places?.Autocomplete === "function";

  const handleLocaleChange = (nextLocale: string) => {
    const safeLocale = (nextLocale || "en") as AppLocale;
    if (safeLocale === locale) return;
    persistPreferredLocale(safeLocale);
    const targetPath = withLocalePrefix(window.location.pathname || "/account", safeLocale);
    window.location.assign(`${targetPath}${window.location.search || ""}${window.location.hash || ""}`);
  };

  useEffect(() => {
    if (user?.username) setNewUsername(user.username);
  }, [user?.username]);

  const handleManageSubscription = async () => {
    try {
      setIsRedirectingPortal(true);
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) throw new Error(t("account.error.notLoggedIn"));

      const response = await fetch("/api/stripe/portal", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error(t("account.error.portalLoadFailed"));

      const { url } = await response.json();
      window.location.href = url;
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsRedirectingPortal(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch("/api/users/request-delete-account", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${session?.access_token}`
        }
      });

      const data = await resp.json().catch(() => ({}));

      if (resp.ok) {
        toast({
          title: t("account.deleteRequestSuccess"),
          variant: "default"
        });
      } else {
        const errorMsg = data.error || "Failed to request account deletion";
        // Check if error is subscription related
        const isSubscriptionError = errorMsg.toLowerCase().includes("abonnement") || errorMsg.toLowerCase().includes("subscription");

        toast({
          title: t("account.deleteError"),
          description: isSubscriptionError ? t("account.subscriptionActiveError") : errorMsg,
          variant: "destructive"
        });
      }
    } catch (error: any) {
      console.error("[Delete Account]", error);
      toast({
        title: t("account.deleteError"),
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleSaveUsername = async () => {
    if (!newUsername.trim()) {
      toast({ title: t("account.error.usernameEmpty"), variant: "destructive" });
      return;
    }

    try {
      setIsSavingUsername(true);
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) throw new Error(t("account.error.notLoggedIn"));

      const response = await fetch(`/api/users/${user?.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ username: newUsername })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || t("account.error.updateUsernameFailed"));
      }

      await refreshAuth?.();
      setIsEditingUsername(false);
      toast({
        title: t("account.usernameUpdated"),
        description: t("account.usernameUpdatedDesc")
      });
    } catch (error: any) {
      toast({
        title: t("account.error.saveFailed"),
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsSavingUsername(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast({
        title: t("account.error.invalidEmail"),
        description: t("account.error.invalidEmailDesc"),
        variant: "destructive"
      });
      return;
    }

    setIsSavingEmail(true);
    try {
      // Supabase handles the security: sends confirmation emails to both old and new address
      const { error } = await supabase.auth.updateUser({ email: newEmail });

      if (error) throw error;

      toast({
        title: t("account.emailConfirmationSent"),
        description: t("account.emailConfirmationSentDesc"),
      });
      setIsChangingEmail(false);
      setNewEmail("");
    } catch (error: any) {
      toast({
        title: t("account.error.changeFailed"),
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsSavingEmail(false);
    }
  };

  const handlePhotoUpload = async (base64: string) => {
    setUploading(true);
    try {
      // Convert base64 to Blob for Supabase
      const byteString = atob(base64.split(',')[1]);
      const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeString });

      const fileExt = mimeString.split('/')[1] || 'jpg';
      const filePath = `${user?.id}/${Math.random()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, blob);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update user profile via API
      // Get fresh session token immediately before request
      const sessionResult = await supabase.auth.getSession();
      const token = sessionResult.data.session?.access_token;

      if (!token) throw new Error(t("account.error.noActiveSession"));

      const response = await fetch(`/api/users/${user?.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ avatarUrl: publicUrl })
      });

      if (!response.ok) throw new Error(t("account.error.updateProfileFailed"));

      await refreshAuth?.();
      toast({
        title: t("account.profileUpdated"),
        description: t("account.profileUpdatedDesc")
      });
    } catch (error: any) {
      console.error("Error uploading photo:", error);
      toast({
        title: t("account.uploadFailed"),
        description: error.message || t("account.uploadFailedDesc"),
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (user?.homeAddress) {
      setHomeInput(user.homeAddress);
    }
  }, [user]);

  // Auth refresh on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      refreshAuth?.();
    }, 300);
    return () => clearTimeout(timer);
  }, [refreshAuth]);

  // Fetch Dashboard Data
  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['dashboard', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      console.log("Fetching dashboard data for user:", user.id);
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`/api/users/${user.id}/dashboard`, {
        headers: { Authorization: `Bearer ${session?.access_token}` }
      });
      if (!res.ok) {
        const text = await res.text();
        console.error("Dashboard fetch failed:", text);
        throw new Error(t("account.error.fetchDashboardFailed"));
      }
      const data = await res.json();
      console.log("Dashboard data received:", {
        favorites: data.favoriteShops?.length,
        wishlist: data.wishlistShops?.length,
        badges: data.badges?.length
      });
      return data;
    },
    enabled: !!user?.id,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 60_000
  });

  // Fetch My Created Routes
  const { data: myRoutes = [], isLoading: isLoadingRoutes } = useQuery({
    queryKey: ['my-routes', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) return [];

      const res = await fetch("/api/routes", {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error(t("account.error.fetchMyRoutesFailed"));
      return res.json();
    },
    enabled: !!user?.id
  });

  const deleteRouteMutation = useMutation({
    mutationFn: async (routeId: string) => {
      const token = await getToken();
      if (!token) throw new Error(t("account.error.notAuthenticated"));

      const res = await fetch(`/api/routes/${routeId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error(t("account.error.deleteRouteFailed"));
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-routes"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      toast({
        title: t("account.routeDeleted"),
        description: t("account.routeDeletedDesc")
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("account.error.deleteFailed"),
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleDeleteRoute = (routeId: string) => {
    deleteRouteMutation.mutate(routeId);
  };

  const deleteItemMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      if (!token) throw new Error(t("account.error.notLoggedIn"));

      const res = await fetch(`/api/wishlist/items/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to delete item");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["my-wishlist"] });
      toast({
        title: t("itemWishlist.deletedTitle"),
        description: t("itemWishlist.deletedDesc"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // NOTE: removed aggressive location-based refetch to avoid request storms.

  // Sync dashboard data to ShopInteractionsContext
  useEffect(() => {
    if (dashboardData) {
      console.log("Syncing dashboard data to interactions:", {
        favorites: dashboardData.favoriteShops?.length,
        wishlist: dashboardData.wishlistShops?.length
      });

      const shopInteractions: Record<string, any> = {};

      dashboardData.favoriteShops?.forEach((shop: any) => {
        shopInteractions[shop.id] = { ...shopInteractions[shop.id], favorite: true };
      });

      dashboardData.wishlistShops?.forEach((shop: any) => {
        shopInteractions[shop.id] = { ...shopInteractions[shop.id], wishlist: true };
      });

      setBulkShopInteractions(shopInteractions);

      // Sync routes
      const routeInteractions: Record<string, any> = {};
      dashboardData.favoriteRoutes?.forEach((route: any) => {
        routeInteractions[route.id] = { ...routeInteractions[route.id], favorite: true };
      });
      dashboardData.wishlistRoutes?.forEach((route: any) => {
        routeInteractions[route.id] = { ...routeInteractions[route.id], wishlist: true };
      });
      setBulkRouteInteractions(routeInteractions);
    }
  }, [dashboardData, setBulkShopInteractions, setBulkRouteInteractions]);

  const handleSignOut = async () => {
    await signOut();
    window.location.href = "/";
  };

  const handleSaveHomeAddress = async () => {
    let finalDetails = { ...addressDetails };

    // Als adres is ingevuld maar coordinaten ontbreken (bijv. handmatig getypt), probeer te geocoden
    if (homeInput && (!finalDetails.lat || !finalDetails.lng) && isLoaded && window.google) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        const res = await geocoder.geocode({ address: homeInput });
        if (res.results && res.results[0] && res.results[0].geometry && res.results[0].geometry.location) {
          const loc = res.results[0].geometry.location;
          finalDetails.lat = loc.lat();
          finalDetails.lng = loc.lng();

          // Probeer ook stad/postcode te vullen als die missen
          if (!finalDetails.city) {
            const city = res.results[0].address_components.find(c => c.types.includes('locality'))?.long_name;
            if (city) finalDetails.city = city;
          }
          if (!finalDetails.postal_code) {
            const pc = res.results[0].address_components.find(c => c.types.includes('postal_code'))?.short_name;
            if (pc) finalDetails.postal_code = pc;
          }
          console.log("Geocoded home address:", finalDetails);
        }
      } catch (err) {
        console.error("Geocoding failed for home address:", err);
      }
    }

    await setHomeAddress(homeInput, finalDetails);
    toast({
      title: t("account.addressSaved"),
      description: t("account.addressSavedDesc"),
    });
  };

  const handlePlaceSelect = () => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) return;

    const placeLat = place.geometry.location?.lat();
    const placeLng = place.geometry.location?.lng();
    const placeAddress = place.formatted_address || place.name || "";
    const placeCity =
      place.address_components?.find((c) => c.types.includes("locality"))?.long_name || "";
    const placePostalCode =
      place.address_components?.find((c) => c.types.includes("postal_code"))?.short_name || "";

    setHomeInput(placeAddress);
    setAddressDetails({
      city: placeCity,
      postalCode: placePostalCode,
      lat: placeLat,
      lng: placeLng
    });
  };

  if (!isLoggedIn) {
    // Basic redirect logic if AuthProvider doesn't handle it yet
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">{t("account.loginRequired")}</h2>
          <Button onClick={() => setLocation("/login")}>{t("account.goToLogin")}</Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isOwner = user?.role === "owner" || user?.role === "admin" || (user?.claimedShopIds && user.claimedShopIds.length > 0);

  // Get tab from URL if present
  const queryParams = new URLSearchParams(window.location.search);
  const initialTab = queryParams.get("tab") || "overview";

  return (
    <div className="min-h-screen bg-background flex flex-col">

      <main className="flex-1 container mx-auto px-4 py-8">
        {/* Profile Header */}
        <div className="flex flex-col md:flex-row gap-6 items-start mb-8 bg-card p-6 rounded-lg shadow-sm border">
          <div className="relative group">
            <Avatar className="w-24 h-24 border-4 border-background shadow-sm">
              <AvatarImage src={user?.avatarUrl || ""} />
              <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                {user?.username?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            {uploading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full text-white">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                <ImageCaptureInput
                  onImage={handlePhotoUpload}
                  label=""
                  className="bg-transparent hover:bg-transparent border-none p-0 h-full w-full opacity-100"
                />
              </div>
            )}
          </div>

          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              {isEditingUsername ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    className="h-10 text-lg font-bold w-48"
                    autoFocus
                  />
                  <Button size="icon" variant="ghost" onClick={handleSaveUsername} disabled={isSavingUsername}>
                    <Check className="h-5 w-5 text-green-600" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => { setIsEditingUsername(false); setNewUsername(user?.username || ""); }}>
                    <X className="h-5 w-5 text-destructive" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 group/edit">
                  <h1 className="text-3xl font-bold">{user?.username}</h1>

                  {/* Badges next to name */}
                  <div className="flex items-center gap-1.5 ml-1">
                    <TooltipProvider>
                      {(dashboardData?.badges || []).map((badge: any) => (
                        <Tooltip key={badge.id || badge.badge_type}>
                          <TooltipTrigger asChild>
                            <div className="cursor-help p-1 hover:bg-muted rounded-full transition-colors">
                              <UserBadges
                                badges={[{ badgeType: badge.badge_type, count: 1 }]}
                                size={20}
                              />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-medium capitalize">{badge.badge_type.replace(/_/g, ' ')}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </TooltipProvider>
                  </div>

                  <Button
                    size="icon"
                    variant="ghost"
                    className="opacity-0 group-hover/edit:opacity-100 transition-opacity h-8 w-8 ml-auto"
                    onClick={() => setIsEditingUsername(true)}
                  >
                    <Edit2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              )}


              {tier === 'power' ? (
                <Badge variant="default" className="bg-primary hover:bg-primary/90 text-white uppercase flex gap-1 items-center">
                  <Crown className="w-3 h-3 fill-current text-white" />
                  Power User
                </Badge>
              ) : (
                <Badge variant={tier === 'pro' ? 'default' : 'secondary'} className="uppercase">
                  {tier === 'pro' ? t("account.tier.insider") : t("account.tier.free")}
                </Badge>
              )}
              {isOwner && <Badge variant="outline">{t("account.ownerBadge")}</Badge>}
            </div>
            <p className="text-muted-foreground">{user?.email}</p>

            <div className="flex gap-4 pt-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <RouteIcon className="h-4 w-4 text-primary" />
                <span>{dashboardData?.topRoutes?.reduce((acc: number, curr: any) => acc + curr.usage_count, 0) || 0} {t("account.stats.rides")}</span>
              </div>
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 text-primary fill-primary" />
                <span>{dashboardData?.latestReviews?.length || 0} {t("account.stats.reviews")}</span>
              </div>
              <div className="flex items-center gap-1">
                <Scan className="h-4 w-4 text-magenta" />
                <span>{dashboardData?.totalScans || 0} {t("account.stats.scans")}</span>
              </div>
            </div>
          </div>

          <Button variant="outline" className="shrink-0" onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" /> {t("account.logout")}
          </Button>
        </div>

        {/* Dashboard Content */}
        <Tabs defaultValue={initialTab} className="space-y-6">
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="overview">{t("account.tabs.profile")}</TabsTrigger>
            <TabsTrigger value="activity">{t("account.tabs.reviews")}</TabsTrigger>
            <TabsTrigger value="social">{t("account.tabs.social")}</TabsTrigger>
            {isOwner && <TabsTrigger value="shop-admin">{t("account.tabs.shopAdmin")}</TabsTrigger>}
            <TabsTrigger value="privacy">{t("account.tabs.privacy")}</TabsTrigger>
            <TabsTrigger value="settings">{t("account.tabs.settings")}</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {/* Content sections removed as per request (moved badges to header) */}
            </div>

            {/* Combined Favorites Section from the old tab */}
            <div className="space-y-8 mt-8 border-t pt-8">
              <section>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-primary fill-current" />
                      {t("account.myGems")}
                    </h3>
                    <Button variant="outline" size="sm" asChild>
                      <Link href={getLocalizedPath("community", locale)}>{t("account.manageGems")}</Link>
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(dashboardData?.latestPosts || []).length > 0 ? (
                      dashboardData.latestPosts.map((post: any) => (
                        <PostCard
                          key={post.id}
                          post={post}
                          onLikeChange={() => queryClient.invalidateQueries({ queryKey: ['dashboard', user?.id] })}
                        />
                      ))
                    ) : (
                      <div className="col-span-full text-center py-8 border border-dashed rounded-lg bg-muted/10">
                        <Sparkles className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                        <p className="text-muted-foreground">{t("community.noGems")}</p>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* Found Wishlist Items Section */}
              <section className="mt-8">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    {t("account.myFinds")}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(dashboardData?.itemWishlist || []).filter((i: any) => i.is_found).length > 0 ? (
                      (dashboardData?.itemWishlist || []).filter((i: any) => i.is_found).map((item: any) => (
                        <div key={item.id} className="p-3 bg-muted/30 rounded-lg flex items-center justify-between border">
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                            <div>
                              <p className="font-medium line-through text-muted-foreground">{item.item_name}</p>
                              {item.found_at && (
                                <p className="text-xs text-muted-foreground">
                                  {t("account.foundOn")} {formatLocalizedDate(item.found_at)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex bg-background border rounded-md">
                            {item.found_post_id && (
                              <Link href={`/community?post=${item.found_post_id}`}>
                                <Button size="icon" variant="ghost" className="text-primary h-8 w-8 hover:bg-muted" title={t("account.openPost")}>
                                  <Camera className="h-4 w-4" />
                                </Button>
                              </Link>
                            )}
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-muted-foreground hover:text-destructive h-8 w-8 hover:bg-muted"
                              title={t("common.delete")}
                              onClick={() => deleteItemMutation.mutate(item.id)}
                              disabled={deleteItemMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-span-1 md:col-span-2 text-center py-8 border border-dashed rounded-lg bg-muted/10">
                        <Sparkles className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                        <p className="text-muted-foreground">{t("account.noItemsFound")}</p>
                        <p className="text-xs text-muted-foreground mt-1">{t("account.goWishlistHint")}</p>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* Shops Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <ExpandableSection
                  title={t("account.favoriteShops")}
                  icon={<Heart className="h-5 w-5 text-primary fill-primary" />}
                  items={dashboardData?.favoriteShops || []}
                  renderItem={(shop: any) => <ShopCard key={shop.id} shop={shop} />}
                  emptyMessage={t("routes.empty.favorites")}
                  initialCount={2}
                  viewAllLink="/shops/favorites"
                />

                <ExpandableSection
                  title={t("account.shopBucketlist")}
                  icon={<Bookmark className="h-5 w-5 text-primary fill-primary" />}
                  items={dashboardData?.wishlistShops || []}
                  renderItem={(shop: any) => <ShopCard key={shop.id} shop={shop} />}
                  emptyMessage={t("account.emptyShopBucketlist")}
                  initialCount={2}
                  viewAllLink="/shops/wishlist"
                />
              </div>

              <ExpandableSection
                title={t("account.myContributions")}
                icon={<PlusCircle className="h-5 w-5 text-primary" />}
                items={dashboardData?.addedShops || []}
                renderItem={(shop: any) => <ShopCard key={shop.id} shop={shop} />}
                emptyMessage={t("account.emptyContributions")}
                initialCount={2}
              />

              {/* Routes Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <ExpandableSection
                  title={t("account.favoriteRoutes")}
                  icon={<RouteIcon className="h-5 w-5 text-primary" />}
                  items={dashboardData?.favoriteRoutes || []}
                  renderItem={(route: any) => <RouteCard key={route.id} route={route} showSaveButton={false} />}
                  emptyMessage={t("routes.empty.favorites")}
                  initialCount={2}
                  viewAllLink="/routes/favorites"
                />

                <ExpandableSection
                  title={t("account.routeBucketlist")}
                  icon={<Bookmark className="h-5 w-5 text-primary fill-primary" />}
                  items={dashboardData?.wishlistRoutes || []}
                  renderItem={(route: any) => <RouteCard key={route.id} route={route} showSaveButton={false} />}
                  emptyMessage={t("account.emptyRouteBucketlist")}
                  initialCount={2}
                  viewAllLink="/routes/wishlist"
                />

                <ExpandableSection
                  title={t("account.myCreatedRoutes")}
                  icon={<MapPin className="h-5 w-5 text-primary" />}
                  items={myRoutes}
                  renderItem={(route: any) => (
                    <RouteCard
                      key={route.id}
                      route={route}
                      showSaveButton={false}
                      onDelete={handleDeleteRoute}
                    />
                  )}
                  emptyMessage={t("account.emptyCreatedRoutes")}
                  initialCount={2}
                  viewAllLink="/routes/my-routes"
                />

                <ExpandableSection
                  title={t("account.myAiScans")}
                  icon={<Scan className="h-5 w-5 text-magenta" />}
                  items={dashboardData?.myScans || []}
                  renderItem={(scan: any) => (
                    <Card key={scan.id} className="overflow-hidden hover:border-magenta/30 transition-all group flex h-24">
                      <div className="w-24 h-24 flex-shrink-0">
                        <img src={scan.first_image_url} className="w-full h-full object-cover" alt="" />
                      </div>
                      <div className="p-3 flex flex-col justify-center overflow-hidden">
                        <h4 className="font-bold text-sm truncate">{scan.ai_title}</h4>
                        <p className="text-xs text-muted-foreground truncate">{scan.full_analysis?.identified_item || t("account.analyzing")}</p>
                        <Link href={`/scanner/results/${scan.id}`} className="mt-1 text-[10px] font-bold text-magenta hover:underline flex items-center gap-1">
                          {t("account.viewResult")} <ArrowRight className="h-2 w-2" />
                        </Link>
                      </div>
                    </Card>
                  )}
                  emptyMessage={t("account.emptyScans")}
                  initialCount={2}
                  viewAllLink="/mijn-scans"
                />
              </div>
            </div>

            {/* Top Routes */}
            <div>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <RouteIcon className="h-5 w-5 text-primary" /> {t("account.myTopRoutes")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Note: UserRouteUsage maps to a route, but dashboard currently returns usage objects. 
                     If the API expanded relations properly we could map data, but for now we might need to rely on what's available.
                     Assuming dashboardData.topRoutes contains route objects if relations worked, or we need to fetch. 
                     Given schema updates without successful push, this might be empty/broken in dev.
                     We'll create a placeholder if empty. */}
                {dashboardData?.topRoutes?.length > 0 ? (
                  dashboardData.topRoutes.map((usage: any) => (
                    usage.route ? (
                      <div key={usage.id} className="border rounded-lg p-4">
                        <h3 className="font-bold">{usage.route.name || t("account.unnamedRoute")}</h3>
                        <p className="text-sm text-muted-foreground">{t("account.routeDrivenCount", { count: usage.usage_count })}</p>
                        {/* Add RouteCard here if we had full route object */}
                      </div>
                    ) : null
                  ))
                ) : (
                  <Card className="col-span-full border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                      <RouteIcon className="h-10 w-10 text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground">{t("account.noRoutesDriven")}</p>
                      <Button variant="ghost" onClick={() => setLocation("/routes")}>{t("account.startRoute")}</Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>



          {/* ACTIVITY TAB */}
          <TabsContent value="activity">
            <div className="space-y-6">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Star className="h-5 w-5 text-primary fill-primary" />
                {t("account.recentReviews")}
              </h3>
              <div className="space-y-4">
                {dashboardData?.latestReviews?.length > 0 ? (
                  dashboardData.latestReviews.map((review: any) => (
                    <Card key={review.id}>
                      <CardContent className="p-4">
                        <div className="flex justify-between mb-2">
                          <span className="font-bold">{review.shop_name || review.shop?.name || t("account.unknownShop")}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatLocalizedDate(review.created_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map(s => (
                              <Star key={s} className={`h-3 w-3 ${s <= ((review.rating_atmosphere + review.rating_service) / 2) ? "text-yellow-400 fill-current" : "text-gray-200"}`} />
                            ))}
                          </div>
                          <div className="flex items-center gap-1 border-l pl-3 ml-1">
                            <Coins className="h-3 w-3 text-muted-foreground" />
                            <PriceLevel level={review.rating_price} size="sm" showLabels={false} />
                          </div>
                        </div>
                        {review.comment && <p className="text-sm">{review.comment}</p>}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <p className="text-muted-foreground">{t("account.noReviews")}</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5 text-primary" />
                    {t("account.settings.title")}
                  </CardTitle>
                  <CardDescription>{t("account.settings.desc")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t("account.username")}</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value)}
                        placeholder={t("account.username")}
                      />
                      <Button onClick={handleSaveUsername} disabled={isSavingUsername || newUsername === user?.username}>
                        {isSavingUsername ? <Loader2 className="h-4 w-4 animate-spin" /> : t("account.save")}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("account.email")}</Label>
                    <div className="flex gap-2">
                      {isChangingEmail ? (
                        <>
                          <Input
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder={t("account.newEmail")}
                            type="email"
                          />
                          <Button onClick={handleUpdateEmail} disabled={isSavingEmail}>
                            {isSavingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : t("account.send")}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setIsChangingEmail(false)}>
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Input value={user?.email || ""} disabled />
                          <Button variant="outline" onClick={() => setIsChangingEmail(true)}>{t("account.edit")}</Button>
                        </>
                      )}
                    </div>
                    {isChangingEmail && <p className="text-xs text-muted-foreground">{t("account.confirmationNote")}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{t("account.homeAddress")}</Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        {!isLoaded ? (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {t("account.loading")}
                          </div>
                        ) : !canUsePlacesAutocomplete ? (
                          <div className="relative">
                            <Input
                              value={homeInput}
                              onChange={(e) => setHomeInput(e.target.value)}
                              placeholder={t("account.homeAddressPlaceholder")}
                              disabled={tier !== 'pro'}
                              className="w-full pr-10"
                            />
                            {homeInput && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="absolute right-0 top-0 h-10 w-10 text-muted-foreground hover:text-destructive"
                                onClick={() => {
                                  setHomeInput("");
                                  setAddressDetails({});
                                  setHomeAddress("", {});
                                  toast({ title: t("account.addressDeleted"), description: t("account.addressDeletedDesc") });
                                }}
                                disabled={tier !== 'pro'}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="relative">
                            <Autocomplete
                              onLoad={(autocomplete) => {
                                autocompleteRef.current = autocomplete;
                              }}
                              onPlaceChanged={handlePlaceSelect}
                              options={{
                                componentRestrictions: { country: "nl" },
                                types: ["address"],
                              }}
                            >
                              <Input
                                value={homeInput}
                                onChange={(e) => setHomeInput(e.target.value)}
                                placeholder={t("account.homeAddressPlaceholder")}
                                disabled={tier !== 'pro'}
                                className="w-full pr-10"
                              />
                            </Autocomplete>
                            {homeInput && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="absolute right-0 top-0 h-10 w-10 text-muted-foreground hover:text-destructive z-10"
                                onClick={() => {
                                  setHomeInput("");
                                  setAddressDetails({});
                                  setHomeAddress("", {});
                                  toast({ title: t("account.addressDeleted"), description: t("account.addressDeletedDesc") });
                                }}
                                disabled={tier !== 'pro'}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                      <Button onClick={handleSaveHomeAddress} disabled={tier !== 'pro' || !homeInput}>{t("account.save")}</Button>
                    </div>
                    {tier !== 'pro' && <p className="text-xs text-muted-foreground">{t("account.insiderOnly")}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>{t("account.languagePreference")}</Label>
                    <Select value={locale} onValueChange={handleLocaleChange}>
                      <SelectTrigger className="max-w-[260px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(LOCALE_LABELS).map(([code, label]) => (
                          <SelectItem key={code} value={code}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t("account.languageSaved")}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-primary" />
                    {t("account.subscription")}
                  </CardTitle>
                  <CardDescription>{t("account.currentStatus", { tier: tier === 'pro' ? t("account.tier.insider") : t("account.tier.free") })}</CardDescription>
                </CardHeader>
                <CardContent>
                  {tier !== 'pro' ? (
                    <div className="space-y-4">
                      <p>
                        {t("account.insiderUpgradeText", { restyleSuffix: canSeeRestyle ? t("account.insiderUpgradeRestyleSuffix") : "" })}
                      </p>
                      <Button onClick={() => setShowUpgradeModal(true)} className="w-full">
                        {t("account.becomeInsider")}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-primary font-medium">
                        <CheckCircle className="h-5 w-5" />
                        {t("account.insiderActive")}
                      </div>

                      {/* Add fallback to downgrade only for dev/demo if needed */}
                      <div className="flex flex-col gap-2">
                        <Button
                          variant="outline"
                          className="w-full justify-start gap-2"
                          onClick={handleManageSubscription}
                          disabled={isRedirectingPortal}
                        >
                          {isRedirectingPortal ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings className="h-4 w-4" />}
                          {t("account.manageSubscription")}
                        </Button>

                        {process.env.NODE_ENV === 'development' && (
                          <Button variant="ghost" onClick={() => setTier('free')} size="sm" className="text-xs">
                            {t("account.devDowngrade")}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Danger Zone */}
              <Card className="border-destructive/20 bg-destructive/5 md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-destructive flex items-center gap-2">
                    <UserX className="h-5 w-5" />
                    {t("account.dangerZone")}
                  </CardTitle>
                  <CardDescription>
                    {t("account.deleteAccountDesc")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive">
                        {t("account.deleteAccount")}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-destructive">{t("account.deleteConfirmTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t("account.deleteConfirmDesc")}
                          <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-md font-medium text-sm">
                            {t("account.deleteWarning")}
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteAccount}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {t("account.deleteAccount")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* SOCIAL TAB */}
          <TabsContent value="social">
            <SocialSettings />
          </TabsContent>

          {/* SHOP ADMIN TAB (Conditional) */}
          {isOwner && (
            <TabsContent value="shop-admin">
              <ShopDashboard />
            </TabsContent>
          )}

          {/* PRIVACY TAB */}
          <TabsContent value="privacy">
            <PrivacyDashboard />
          </TabsContent>

        </Tabs>
      </main>

      <Footer />
      <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} />
    </div >
  );
}

function CheckCircle(props: any) {
  return <Award {...props} />; // Placeholder
}

function ExpandableSection<T>({ title, icon, items, renderItem, emptyMessage, initialCount = 6, viewAllLink }: {
  title: string;
  icon: React.ReactNode;
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  emptyMessage: string;
  initialCount?: number;
  viewAllLink?: string;
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const visibleItems = expanded ? items : items.slice(0, initialCount);
  const showButton = items.length > initialCount;

  return (
    <section>
      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
        {icon} {title}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {items.length > 0 ? (
          visibleItems.map(renderItem)
        ) : (
          <p className="col-span-full text-muted-foreground">{emptyMessage}</p>
        )}
      </div>
      {showButton && (
        <div className="mt-6 flex justify-center">
          {viewAllLink ? (
            <Button variant="outline" className="gap-2" asChild>
              <Link to={viewAllLink}>
                <ArrowRight className="h-4 w-4" /> {t("common.viewAll", { count: items.length })}
              </Link>
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => setExpanded(!expanded)}
              className="gap-2"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-4 w-4" /> {t("common.showLess")}
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" /> {t("common.showAll", { count: items.length })}
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
